(() => {
  const DETECTION_TIMEOUT = 5000;
  const root = document.getElementById('miami-shipping');
  if (!root || root.dataset.miamiInitialized === 'true') return;
  root.dataset.miamiInitialized = 'true';
  const dialog = root.querySelector('#miami-shipping-dialog');
  let verified = false;
  let generation = 0;
  let controller;
  let detectionTimer;
  let returnFocus;
  let originalOverflow;
  let carouselTimer;
  let carouselDeadline = 0;
  let carouselSyncQueued = false;
  let cityResizeFrame;
  let resumeCityOnVisibility = document.visibilityState === 'hidden';
  const citySwipers = new WeakSet();

  function syncCityDestination(scope = document) {
    const destinations = [...scope.querySelectorAll('[data-miami-city-destination]')];
    if (scope instanceof Element && scope.matches('[data-miami-city-destination]')) destinations.unshift(scope);
    const location = verified ? window.__miamiShippingCity?.snapshot() : null;
    for (const element of destinations) {
      const fallback = element.dataset.miamiCityFallback || '';
      const template = element.dataset.miamiCityTemplate || '';
      const text = location?.countryCode === 'US' && template.includes('__MIAMI_CITY__') ?
        template.replaceAll('__MIAMI_CITY__', location.city) : fallback;
      if (element.textContent !== text) element.textContent = text;
      if (location && element.getClientRects().length) {
        const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
        // Long names retain the generic destination instead of pushing the
        // delivery offer and button over the product artwork.
        if (Number.isFinite(lineHeight) && element.getBoundingClientRect().height > lineHeight * 2 + 1) {
          element.textContent = fallback;
        }
      }
    }
  }

  function scheduleCityDestination() {
    cancelAnimationFrame(cityResizeFrame);
    // Swiper adjusts its slide widths after the browser resize event.
    cityResizeFrame = requestAnimationFrame(() => {
      cityResizeFrame = requestAnimationFrame(() => {
        cityResizeFrame = undefined;
        syncCityDestination();
      });
    });
  }

  function refreshCarousel(swiper) {
    swiper.update();
    swiper.pagination?.render();
    swiper.pagination?.update();
    swiper.navigation?.update();
  }

  function showFirstCarouselSlide(swiper) {
    const index = swiper.params.loop ? swiper.getSlideIndexByData(0) : 0;
    // Complete the zero-duration move before restarting autoplay. slideToLoop
    // defers this move to a frame and can otherwise pause autoplay afterwards.
    swiper.slideTo(Number.isFinite(index) ? index : 0, 0, false);
  }

  function removeCarouselSlides() {
    clearTimeout(carouselTimer);
    carouselDeadline = 0;
    const containers = new Set();
    for (const slide of document.querySelectorAll('[data-miami-slide]')) {
      // Hide synchronously, before rebuilding the loop or a persisted page snapshot.
      slide.hidden = true;
      slide.style.display = 'none';
      const container = slide.closest('.classic-container[data-miami-carousel-id]');
      if (container) containers.add(container);
      else slide.remove();
    }
    for (const container of containers) {
      const swiper = container.swiper;
      const activeSwiper = swiper && !swiper.destroyed;
      const autoplayWasRunning = activeSwiper && swiper.autoplay?.running;
      try {
        if (autoplayWasRunning) swiper.autoplay.stop();
        if (activeSwiper && swiper.params.loop) swiper.loopDestroy();
        container.querySelectorAll('[data-miami-slide]').forEach(slide => slide.remove());
        if (activeSwiper) {
          swiper.recalcSlides();
          swiper.update();
          if (swiper.params.loop) swiper.loopCreate(0);
          refreshCarousel(swiper);
          showFirstCarouselSlide(swiper);
        }
      } catch {
        // No carousel failure may leave a geographically restricted slide in the DOM.
        container.querySelectorAll('[data-miami-slide]').forEach(slide => slide.remove());
      } finally {
        if (autoplayWasRunning && !swiper.destroyed) swiper.autoplay.start();
      }
    }
  }

  function syncCarousels() {
    clearTimeout(carouselTimer);
    if (!verified || document.visibilityState === 'hidden') return;
    let awaitingSwiper = false;
    for (const template of document.querySelectorAll('template[data-miami-slide-template]')) {
      const container = [...document.querySelectorAll('.classic-container[data-miami-carousel-id]')]
        .find(element => element.dataset.miamiCarouselId === template.dataset.miamiCarouselId);
      if (!container || container.querySelector('[data-miami-slide]')) continue;
      const swiper = container.swiper;
      if (!swiper || swiper.destroyed || !swiper.initialized || typeof swiper.prependSlide !== 'function') {
        awaitingSwiper = true;
        continue;
      }
      if (!citySwipers.has(swiper) && typeof swiper.on === 'function') {
        citySwipers.add(swiper);
        swiper.on('resize', scheduleCityDestination);
      }
      const slide = template.content.firstElementChild?.cloneNode(true);
      if (!slide?.matches('.swiper-slide[data-miami-slide]')) continue;
      syncCityDestination(slide);
      const autoplayWasRunning = swiper.autoplay?.running;
      try {
        if (autoplayWasRunning) swiper.autoplay.stop();
        swiper.prependSlide(slide);
        syncCityDestination(slide);
        refreshCarousel(swiper);
        showFirstCarouselSlide(swiper);
      } catch {
        slide.remove();
        try { refreshCarousel(swiper); } catch { /* Keep the campaign absent. */ }
        awaitingSwiper = true;
      } finally {
        if (autoplayWasRunning && !swiper.destroyed) swiper.autoplay.start();
      }
    }
    if (awaitingSwiper && performance.now() < carouselDeadline) {
      carouselTimer = setTimeout(syncCarousels, 250);
    }
  }

  function scheduleCarouselSync(restartReadiness = false) {
    if (!verified) return;
    if (restartReadiness) carouselDeadline = performance.now() + 10000;
    if (carouselSyncQueued) return;
    carouselSyncQueued = true;
    queueMicrotask(() => {
      carouselSyncQueued = false;
      syncCarousels();
    });
  }

  function visible(element) {
    if (!element || !element.getClientRects().length) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return [...element.getClientRects()].some(rect => rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth);
  }

  function anotherOverlay() {
    const selectors = ['dialog[open]', '[aria-modal="true"]', '[role="dialog"]', 'cart-drawer.active', 'menu-drawer details[open]', 'header-drawer details[open]', 'details-modal details[open]', 'modal-dialog[open]', '.modal.show', '.newsletter_popup:not(.close_popup)', '.cookie-popup', '[data-testid="POPUP"]', '[data-testid="POPUP_CONTAINER"]', '.ecom-popup-show', '.employee-editor-overlay.show', '#editor-overlay', '.vip-success-overlay.show', '#transcy-geo.transcy-show', '.transcy-switcher.transcy-show'];
    if ([...document.querySelectorAll(selectors.join(','))].some(element => !root.contains(element) && visible(element))) return true;
    return [document.documentElement, document.body].some(element => element.classList.contains('transcy-modal-opened') || element.classList.contains('transcy-popup-opened')) || document.body.classList.contains('overflow-hidden') || document.body.classList.contains('menu-open');
  }

  function closeDetails(restoreFocus = true) {
    if (dialog?.open) dialog.close();
    if (originalOverflow !== undefined) {
      document.documentElement.style.overflow = originalOverflow;
      originalOverflow = undefined;
    }
    const focus = returnFocus;
    returnFocus = null;
    if (restoreFocus && focus?.isConnected && visible(focus)) focus.focus({ preventScroll: true });
  }

  function syncElements(scope = document) {
    const nodes = [...scope.querySelectorAll('[data-miami-us-only]')];
    if (scope instanceof Element && scope.matches('[data-miami-us-only]')) nodes.unshift(scope);
    for (const element of nodes) {
      if (element.hidden === verified) element.hidden = !verified;
      if (verified) {
        if (element.style.display === 'none') element.style.removeProperty('display');
      } else if (element.style.display !== 'none') {
        element.style.display = 'none';
      }
    }
  }

  function hide(resetCity = true) {
    verified = false;
    if (resetCity) window.__miamiShippingCity?.reset();
    generation += 1;
    clearTimeout(detectionTimer);
    cancelAnimationFrame(cityResizeFrame);
    controller?.abort();
    removeCarouselSlides();
    document.documentElement.removeAttribute('data-miami-country');
    closeDetails(false);
    syncElements();
  }

  async function detect(preserveDocumentCity = false) {
    hide(!preserveDocumentCity);
    if (document.visibilityState === 'hidden') return;
    const current = generation;
    const startedAt = performance.now();
    controller = new AbortController();
    const signal = controller.signal;
    detectionTimer = setTimeout(() => { if (generation === current) hide(); }, DETECTION_TIMEOUT);
    try {
      const route = window.Shopify?.routes?.root || root.dataset.routeRoot || '/';
      const endpoint = new URL(route.replace(/\/?$/, '/') + 'browsing_context_suggestions.json', location.origin);
      if (endpoint.origin !== location.origin) throw new Error('Invalid route');
      endpoint.searchParams.set('country[enabled]', 'true');
      let response;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          response = await fetch(endpoint.href, { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'application/json' } });
        } catch (error) {
          if (attempt > 0 || signal.aborted || current !== generation) throw error;
          response = null;
        }
        if (response?.ok) break;
        const transient = !response || (response.status >= 500 && response.status <= 599);
        if (!transient || attempt > 0) throw new Error('Detection unavailable');
        // One retry shares the original deadline and abort signal; it cannot
        // extend verification or reuse a country from an earlier lifecycle.
        await new Promise(resolve => setTimeout(resolve, 150));
        if (signal.aborted || current !== generation || performance.now() - startedAt >= DETECTION_TIMEOUT || document.visibilityState === 'hidden') return;
      }
      if (!response?.ok) throw new Error('Detection unavailable');
      const result = await response.json();
      if (signal.aborted || current !== generation || performance.now() - startedAt >= DETECTION_TIMEOUT || document.visibilityState === 'hidden') return;
      clearTimeout(detectionTimer);
      if (result?.detected_values?.country?.handle !== 'US') {
        window.__miamiShippingCity?.reset();
        return;
      }
      verified = true;
      document.documentElement.setAttribute('data-miami-country', 'US');
      syncElements();
      scheduleCarouselSync(true);
    } catch {
      if (generation === current) hide();
    }
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-miami-open-details]');
    if (trigger && verified && visible(trigger) && document.visibilityState !== 'hidden' && !anotherOverlay() && typeof dialog?.showModal === 'function') {
      event.preventDefault();
      returnFocus = trigger;
      try {
        dialog.showModal();
        originalOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        dialog.querySelector('[data-miami-close]')?.focus({ preventScroll: true });
      } catch { closeDetails(false); }
      return;
    }
    if (event.target.closest('[data-miami-close]') && root.contains(event.target)) closeDetails();
  });
  dialog?.addEventListener('cancel', event => { event.preventDefault(); closeDetails(); });
  dialog?.addEventListener('close', () => { if (!dialog.open) closeDetails(); });
  dialog?.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDetails();
  });
  document.addEventListener('miami:city', () => syncCityDestination());
  window.addEventListener('resize', scheduleCityDestination);
  document.fonts?.ready.then(scheduleCityDestination).catch(() => {});
  window.addEventListener('pagehide', () => {
    resumeCityOnVisibility = false;
    hide();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      resumeCityOnVisibility = false;
      detect();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      resumeCityOnVisibility = true;
      hide(false);
    } else {
      const preserveCity = resumeCityOnVisibility;
      resumeCityOnVisibility = false;
      detect(preserveCity);
    }
  });
  document.addEventListener('DOMContentLoaded', () => scheduleCarouselSync(true));
  document.addEventListener('shopify:section:load', () => scheduleCarouselSync(true));
  document.addEventListener('shopify:section:select', () => scheduleCarouselSync(true));
  new MutationObserver(records => {
    for (const record of records) {
      // Some page builders replace all slide children while retaining Swiper.
      // A combined replacement differs from Swiper's individual loop moves.
      if (record.type === 'childList' && record.addedNodes.length && record.removedNodes.length &&
          record.target instanceof Element && record.target.matches('.swiper-wrapper') &&
          record.target.parentElement?.matches('.classic-container[data-miami-carousel-id]') &&
          [...record.removedNodes].some(node => node instanceof Element && node.matches('[data-miami-slide]')) &&
          !record.target.querySelector('[data-miami-slide]')) scheduleCarouselSync();
      if (record.type === 'childList') for (const node of record.addedNodes) if (node instanceof Element) {
        syncElements(node);
        if (node.matches('template[data-miami-slide-template],.classic-container[data-miami-carousel-id]') || node.querySelector('template[data-miami-slide-template],.classic-container[data-miami-carousel-id]')) scheduleCarouselSync(true);
      }
      if (record.type === 'attributes' && record.attributeName === 'class' && record.target.matches('.classic-container[data-miami-carousel-id].swiper-initialized')) scheduleCarouselSync();
    }
    if (dialog?.open && anotherOverlay()) closeDetails(false);
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'style', 'hidden', 'aria-hidden', 'aria-modal'] });
  // An early response from this document can precede this deferred controller.
  detect(true);
})();
